import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import {
  Search,
  RotateCcw,
  Eye,
  CircleCheck,
  CircleX,
  CreditCard,
  Hash,
  Building2,
  Landmark,
  Banknote,
  Wallet,
  ShieldCheck,
  ShieldX,
  X,
  History,
} from "lucide-react";
import DashboardLayout from "../../components/layout/index";

const { RangePicker } = DatePicker;

const STATUS_OPTIONS = ["Pending", "Approved", "Rejected"];

// Backend validateStatus codes: P = Pending, A = Approved, R = Rejected.
const STATUS_CODE_MAP = { Pending: "P", Approved: "A", Rejected: "R" };
const STATUS_LABEL_MAP = { P: "Pending", A: "Approved", R: "Rejected" };

const mapApiRequest = (item) => ({
  requestId: item.id,
  doctorCode: item.doctorCode || "",
  name: item.bankAccountName || "",
  panNumber: item.panNumber || "",
  bankName: item.bankName || "",
  ifscCode: item.bankIfsc || "",
  accountNumber: item.bankAccountNumber || "",
  requestDate: item.requestDateTime ? item.requestDateTime.split("T")[0] : "",
  requestDateTime: item.requestDateTime || "",
  requestedAmount: item.amount,
  tdsAmount: item.tdsAmount,
  creditedAmount: item.creditedAmount,
  status: STATUS_LABEL_MAP[item.validateStatus] || item.validateStatus || "Pending",
  validateBy: item.validateBy || "",
  validateDateTime: item.validateDateTime || "",
  validateRemarks: item.validateRemarks || "",
  panValid: null,
});

const formatDate = (value) => {
  if (!value) return "-";
  const [year, month, day] = value.split("-");
  return year && month && day ? `${day}-${month}-${year}` : value;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

const formatCurrency = (value) => `₹${Number(value || 0).toFixed(1)}`;

const getValidateBy = () => {
  try {
    const firstName = localStorage.getItem("userId") || "";
    const lastName = localStorage.getItem("lastName") || "";
    return `${firstName} ${lastName}`.trim() || "";
  } catch {
    return "admin";
  }
};

const statusBadgeClasses = (status) => {
  if (status === "Approved") return "bg-emerald-100 text-emerald-700";
  if (status === "Rejected") return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
};

const DetailItem = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 text-sm text-slate-600">
    <Icon size={14} className="shrink-0 text-slate-400" />
    <span className="truncate">{children}</span>
  </span>
);

const DEFAULT_FROM_DATE = dayjs().subtract(30, "day").format("YYYY-MM-DD");
const DEFAULT_TO_DATE = dayjs().format("YYYY-MM-DD");

function CurebayDoctorPayments() {
  const API_URL = process.env.REACT_APP_NEW_DOCTOR_URL;
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(DEFAULT_FROM_DATE);
  const [toDate, setToDate] = useState(DEFAULT_TO_DATE);
  const [status, setStatus] = useState("Pending");
  const [viewDetails, setViewDetails] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState(null);
  const [processing, setProcessing] = useState({ id: null, action: null });
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectRemarks, setRejectRemarks] = useState("");

  const fetchRequests = async () => {
    if (!fromDate || !toDate) return;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (status) {
        params.set("VALIDATESTATUS", STATUS_CODE_MAP[status] || status);
      }
      params.set("fromDate", fromDate);
      params.set("toDate", toDate);

      const res = await fetch(
        `${API_URL}/doctor/DoctorRequestForTakePayment/requests?${params.toString()}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      const rows = Array.isArray(data) ? data : [];
      setRequests(rows.map(mapApiRequest));
    } catch (error) {
      console.error("Failed to fetch doctor payment requests:", error);
      setRequests([]);
      toast.error("Failed to load payment requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, status]);

  const handleReset = () => {
    setSearch("");
    setFromDate(DEFAULT_FROM_DATE);
    setToDate(DEFAULT_TO_DATE);
    setStatus("Pending");
  };

  const filteredRequests = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    if (!searchTerm) return requests;
    return requests.filter(
      (req) =>
        (req.name || "").toLowerCase().includes(searchTerm) ||
        (req.doctorCode || "").toLowerCase().includes(searchTerm) ||
        (req.panNumber || "").toLowerCase().includes(searchTerm)
    );
  }, [requests, search]);

  const updateRequest = (requestId, updates) => {
    setRequests((prev) =>
      prev.map((req) => (req.requestId === requestId ? { ...req, ...updates } : req))
    );
  };

  const validateRequest = async (req, validateStatus, action, remarks = "") => {
    try {
      setProcessing({ id: req.requestId, action });
      const res = await fetch(
        `${API_URL}/doctor/DoctorRequestForTakePayment/${req.requestId}/validate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            validateStatus,
            validateBy: getValidateBy(),
            validateRemarks: remarks,
          }),
        }
      );

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      return true;
    } catch (error) {
      console.error("Failed to validate payment request:", error);
      return false;
    } finally {
      setProcessing({ id: null, action: null });
    }
  };

  const handleApprove = async (req) => {
    const ok = await validateRequest(req, "A", "approve");
    if (ok) {
      toast.success(`Request #${req.requestId} approved.`);
      fetchRequests();
    } else {
      toast.error("Failed to approve request.");
    }
  };

  const openRejectModal = (req) => {
    setRejectTarget(req);
    setRejectRemarks("");
  };

  const closeRejectModal = () => {
    setRejectTarget(null);
    setRejectRemarks("");
  };

  const handleConfirmReject = async () => {
    if (!rejectTarget) return;
    const req = rejectTarget;
    const ok = await validateRequest(req, "R", "reject", rejectRemarks.trim());
    if (ok) {
      toast.error(`Request #${req.requestId} rejected.`);
      closeRejectModal();
      fetchRequests();
    } else {
      toast.error("Failed to reject request.");
    }
  };

  // TODO: wire PAN verification actions up to a real endpoint once available.
  const handleValidPan = (req) => {
    updateRequest(req.requestId, { panValid: true });
    toast.success("Marked PAN as valid.");
  };

  const handleInvalidPan = (req) => {
    updateRequest(req.requestId, { panValid: false });
    toast.error("Marked PAN as invalid.");
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-slate-50 p-4 md:pt-10">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center rounded-xl border border-emerald-100 bg-emerald-50 p-2.5">
            <Wallet size={26} className="text-emerald-600" />
          </div>
          <h2 className="text-lg font-bold tracking-tight text-slate-800 md:text-xl">
            Doctor Payments
          </h2>
        </div>

        {/* ────────────────────────────────────────────────────────────────────── */}
        {/* FILTERS */}
        {/* ────────────────────────────────────────────────────────────────────── */}
        {/* <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-6"> */}
            {/* <div className="flex flex-col md:col-span-2">
              <label className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Search
              </label>
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by Name / Doctor Code / PAN"
                  className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#3cba84]"
                />
              </div>
            </div> */}

            {/* <div className="flex flex-col md:col-span-2">
              <label className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Date Range
              </label>
              <RangePicker
                value={[fromDate ? dayjs(fromDate) : null, toDate ? dayjs(toDate) : null]}
                onChange={(dates) => {
                  setFromDate(dates && dates[0] ? dates[0].format("YYYY-MM-DD") : "");
                  setToDate(dates && dates[1] ? dates[1].format("YYYY-MM-DD") : "");
                }}
                format="DD-MM-YYYY"
                className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm shadow-sm"
                allowClear
              />
            </div> */}

            {/* <div className="flex flex-col">
              <label className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#3cba84]"
              >
                <option value="">All</option>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div> */}

            {/* <div className="flex flex-col">
              <label className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                &nbsp;
              </label>
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                <RotateCcw size={14} />
                Reset
              </button>
            </div> */}
          {/* </div>
        </div> */}

        {/* ────────────────────────────────────────────────────────────────────── */}
        {/* REQUEST CARDS */}
        {/* ────────────────────────────────────────────────────────────────────── */}
        <div className="mt-6 space-y-5">
          {loading ? (
            <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-slate-500 shadow-sm">
              Loading payment requests...
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-slate-500 shadow-sm">
              No payment requests found.
            </div>
          ) : (
            filteredRequests.map((req) => {
              const initial = req.name ? req.name.trim().charAt(0).toUpperCase() : "N/A";
              return (
                <div
                  key={req.requestId}
                  className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md"
                >
                  <div className="p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex items-start gap-4">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-blue-50 text-sm font-bold text-blue-700 ring-1 ring-blue-100">
                          {initial}
                        </div>
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-base font-semibold text-slate-900">
                              {req.name || "N/A"}
                            </span>
                            <span
                              className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusBadgeClasses(
                                req.status
                              )}`}
                            >
                              {req.status}
                            </span>
                          </div>

                          <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                            <DetailItem icon={Hash}>{req.doctorCode || "-"}</DetailItem>
                            <DetailItem icon={CreditCard}>{req.panNumber || "-"}</DetailItem>
                            <DetailItem icon={Building2}>{req.bankName || "-"}</DetailItem>
                            <DetailItem icon={Landmark}>{req.ifscCode || "-"}</DetailItem>
                            <DetailItem icon={Banknote}>{req.accountNumber || "-"}</DetailItem>
                          </div>
                        </div>
                      </div>
                      <span className="shrink-0 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                        Request Id: {req.requestId}
                      </span>
                    </div>

                    {/* Withdraw Details */}
                    <div className="mt-4 rounded-lg bg-slate-50 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-slate-800">
                          Withdraw Details
                        </h3>
                        <span className="text-xs font-medium text-slate-500">
                          Request Date: {formatDate(req.requestDate)}
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-3 divide-x divide-slate-200 text-center sm:text-left">
                        <div className="pr-4 sm:pl-0">
                          <p className="text-xs text-slate-500">Requested Amount</p>
                          <p className="mt-0.5 text-sm font-semibold text-slate-900">
                            {formatCurrency(req.requestedAmount)}
                          </p>
                        </div>
                        <div className="px-4">
                          <p className="text-xs text-slate-500">TDS Amount</p>
                          <p className="mt-0.5 text-sm font-semibold text-slate-900">
                            {formatCurrency(req.tdsAmount)}
                          </p>
                        </div>
                        <div className="pl-4">
                          <p className="text-xs text-slate-500">Credited Amount</p>
                          <p className="mt-0.5 text-sm font-semibold text-emerald-600">
                            {formatCurrency(req.creditedAmount)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-slate-50/60 px-5 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setViewDetails(req)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#004276] px-3.5 py-2 text-sm font-medium text-white transition hover:bg-[#003a5c]"
                      >
                        <Eye size={15} />
                        View Details
                      </button>
                      {/* <button
                        type="button"
                        onClick={() => setPaymentHistory(req)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#004276] px-3.5 py-2 text-sm font-medium text-white transition hover:bg-[#003a5c]"
                      >
                        <History size={15} />
                        Payment History
                      </button> */}
                      {/* <button
                        type="button"
                        onClick={() => handleValidPan(req)}
                        disabled={req.panValid === true}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#004276] px-3.5 py-2 text-sm font-medium text-white transition hover:bg-[#003a5c] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <ShieldCheck size={15} />
                        {req.panValid === true ? "PAN Valid" : "Valid PAN"}
                      </button> */}
                      {/* <button
                        type="button"
                        onClick={() => handleInvalidPan(req)}
                        disabled={req.panValid === false}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#004276] px-3.5 py-2 text-sm font-medium text-white transition hover:bg-[#003a5c] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <ShieldX size={15} />
                        {req.panValid === false ? "PAN Invalid" : "Invalid PAN?"}
                      </button> */}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleApprove(req)}
                        disabled={req.status === "Approved" || processing.id === req.requestId}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <CircleCheck size={15} />
                        {processing.id === req.requestId && processing.action === "approve"
                          ? "Approving..."
                          : "Approve"}
                      </button>
                      <button
                        type="button"
                        onClick={() => openRejectModal(req)}
                        disabled={req.status === "Rejected" || processing.id === req.requestId}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <CircleX size={15} />
                        {processing.id === req.requestId && processing.action === "reject"
                          ? "Rejecting..."
                          : "Reject"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {viewDetails && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setViewDetails(null)}
        >
          <div
            className="relative w-full max-w-lg rounded-xl bg-white p-5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#004276]">
                Request #{viewDetails.requestId} Details
              </h3>
              <button
                type="button"
                onClick={() => setViewDetails(null)}
                className="text-slate-500 hover:text-slate-700"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <tbody>
                  {[
                    ["Name", viewDetails.name || "N/A"],
                    ["Doctor Code", viewDetails.doctorCode || "-"],
                    ["PAN Number", viewDetails.panNumber || "-"],
                    ["Bank Name", viewDetails.bankName || "-"],
                    ["IFSC Code", viewDetails.ifscCode || "-"],
                    ["Account Number", viewDetails.accountNumber || "-"],
                    ["Requested Amount", formatCurrency(viewDetails.requestedAmount)],
                    ["TDS Amount", formatCurrency(viewDetails.tdsAmount)],
                    ["Credited Amount", formatCurrency(viewDetails.creditedAmount)],
                    ["Request Date", formatDate(viewDetails.requestDate)],
                    ["Status", viewDetails.status],
                    ["Validated By", viewDetails.validateBy || "-"],
                    ["Validated Date", formatDateTime(viewDetails.validateDateTime)],
                    ["Remarks", viewDetails.validateRemarks || "-"],
                  ].map(([label, value]) => (
                    <tr key={label} className="border-b border-gray-100 last:border-b-0">
                      <td className="whitespace-nowrap px-3 py-2 align-top font-medium text-[#004276]">
                        {label}
                      </td>
                      <td className="break-words px-3 py-2 text-slate-700">{String(value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setViewDetails(null)}
                className="rounded-lg bg-[#004276] px-4 py-2 text-sm font-medium text-white hover:bg-[#003a5c]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {paymentHistory && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setPaymentHistory(null)}
        >
          <div
            className="relative w-full max-w-lg rounded-xl bg-white p-5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#004276]">
                Payment History — {paymentHistory.name || "N/A"}
              </h3>
              <button
                type="button"
                onClick={() => setPaymentHistory(null)}
                className="text-slate-500 hover:text-slate-700"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* TODO: wire up to a dedicated payment-history-by-doctor endpoint once available. */}
            {paymentHistory.history && paymentHistory.history.length > 0 ? (
              <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Date
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Amount Withdrawn
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.history.map((entry, idx) => (
                      <tr key={`${entry.date}-${idx}`} className="border-b border-gray-100 last:border-b-0">
                        <td className="px-3 py-2 text-slate-700">{formatDate(entry.date)}</td>
                        <td className="px-3 py-2 font-medium text-slate-900">
                          {formatCurrency(entry.amount)}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusBadgeClasses(
                              entry.status
                            )}`}
                          >
                            {entry.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                No previous withdrawal history found.
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setPaymentHistory(null)}
                className="rounded-lg bg-[#004276] px-4 py-2 text-sm font-medium text-white hover:bg-[#003a5c]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={closeRejectModal}
        >
          <div
            className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#004276]">
                Reject Request #{rejectTarget.requestId}
              </h3>
              <button
                type="button"
                onClick={closeRejectModal}
                className="text-slate-500 hover:text-slate-700"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">
              Remarks
            </label>
            <textarea
              value={rejectRemarks}
              onChange={(e) => setRejectRemarks(e.target.value)}
              rows={4}
              placeholder="Enter reason for rejection"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#3cba84]"
            />

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeRejectModal}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmReject}
                disabled={processing.id === rejectTarget.requestId && processing.action === "reject"}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {processing.id === rejectTarget.requestId && processing.action === "reject"
                  ? "Rejecting..."
                  : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default CurebayDoctorPayments;
